<template>
  <div>
    <v-row v-if="isLoading">
      <v-col cols="12">
        <v-skeleton-loader type="list-item" />
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <!-- Read-only mode: show descriptions only -->
        <div v-if="readOnly">
          <div
            v-for="scope in selectedScopesDetails"
            :key="scope.value"
            class="mb-3"
          >
            <div class="font-weight-medium">{{ scope.description }}</div>
            <div
              class="text-caption text-medium-emphasis"
              v-if="scope.help_text"
            >
              {{ scope.help_text }}
            </div>
          </div>
        </div>
        <!-- Edit mode: show checkboxes -->
        <div v-else>
          <div v-for="scope in availableScopes" :key="scope.value" class="mb-3">
            <v-checkbox
              v-model="selectedScopes"
              :label="scope.description"
              :value="scope.value"
              :disabled="isCheckboxDisabled(scope.value)"
              class="mt-0 pt-0"
              hide-details
            />
            <div
              class="text-caption text-secondary ml-8"
              v-if="scope.help_text"
            >
              {{ scope.help_text }}
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Warning Dialog -->
    <v-dialog v-model="showWarningDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon color="warning" class="mr-2" :icon="mdiAlert" />
          Atenção
        </v-card-title>
        <v-card-text>
          <p>
            <strong>Ao remover este escopo de custódia:</strong>
          </p>
          <ul class="mt-2">
            <li>
              Dispositivos que utilizam este escopo podem parar de realizar a
              custódia de documentos completamente
            </li>
            <li>
              Será necessário reautenticar os dispositivos afetados para que
              voltem a funcionar
            </li>
            <li>
              A funcionalidade de custódia pode ficar indisponível nos
              dispositivos afetados até a reconfiguração
            </li>
          </ul>
          <p class="mt-3">Tem certeza de que deseja continuar?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="cancelScopeChange"> Cancelar </v-btn>
          <v-btn color="warning" @click="confirmScopeChange"> Continuar </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { mdiAlert } from "@mdi/js";
import { useCustodyScopesQuery } from "@/queries/modules/scopes";
import { useSnackbar } from "@/plugins/snackbar";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const snackbar = useSnackbar();

// State
const showWarningDialog = ref(false);
const pendingChange = ref(null);

// TanStack Query - fetch available custody scopes
const { data: scopesData, isLoading, error } = useCustodyScopesQuery();

// Computed
const availableScopes = computed(() => scopesData.value || []);

const selectedScopes = computed({
  get() {
    return props.modelValue || [];
  },
  set(newValue) {
    // Check if we're removing a scope (not adding)
    const currentValue = props.modelValue || [];
    const removedScopes = currentValue.filter(
      (scope) => !newValue.includes(scope),
    );

    if (removedScopes.length > 0 && !props.readOnly) {
      // Show warning dialog for scope removal
      pendingChange.value = newValue;
      showWarningDialog.value = true;
    } else {
      // Direct change (adding scope or in read-only mode)
      emit("update:modelValue", newValue);
    }
  },
});

const selectedScopesDetails = computed(() => {
  return availableScopes.value.filter((scope) =>
    props.modelValue.includes(scope.value),
  );
});

// Watch for errors
watch(error, (err) => {
  if (err) {
    snackbar.error("Erro ao carregar escopos disponíveis: " + err.message);
  }
});

// Methods
const isCheckboxDisabled = (scopeValue) => {
  // Only disable if the entire component is disabled OR if it's the custody scope
  if (props.disabled) return true;
  if (scopeValue === "custody") return true;
  return false;
};

const confirmScopeChange = () => {
  // User confirmed, apply the pending change
  showWarningDialog.value = false;
  emit("update:modelValue", pendingChange.value);
  pendingChange.value = null;
};

const cancelScopeChange = () => {
  // User cancelled, discard the pending change
  showWarningDialog.value = false;
  pendingChange.value = null;
  // Force re-render by emitting the current value
  emit("update:modelValue", [...(props.modelValue || [])]);
};
</script>
